import { useState } from "react";
import { Check, Copy, MoreVertical, PlusSquare, Share } from "lucide-react";
import { GlowButton, Sheet } from "@/components/ui";
import { useInstall, useInstallGuide } from "./useInstall";

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary/20 text-sm font-semibold text-[var(--primary-text)]">
        {n}
      </span>
      <span className="pt-0.5 text-sm leading-relaxed text-ink-soft">{children}</span>
    </li>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <span className="mx-0.5 inline-flex items-center gap-1 rounded-md border border-line bg-fg/[0.06] px-1.5 py-0.5 align-middle text-xs font-medium text-ink">
      {children}
    </span>
  );
}

function CopyLink() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      /* clipboard blocked: the address is shown on screen */
    }
  };
  return (
    <GlowButton
      variant="ghost"
      fullWidth
      onClick={() => void copy()}
      leftIcon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
    >
      {copied ? "Link copied" : "Copy the link"}
    </GlowButton>
  );
}

/** How to add RealityStore to the home screen where the browser can't do it in one tap. */
export function InstallGuide() {
  const { open, setOpen } = useInstallGuide();
  const { platform } = useInstall();
  const close = () => setOpen(false);
  const host = typeof window === "undefined" ? "" : window.location.host;

  let body: React.ReactNode;
  if (platform === "ios-safari") {
    body = (
      <ol className="flex flex-col gap-4">
        <Step n={1}>
          Tap the Share button{" "}
          <Key>
            <Share className="size-3.5" aria-hidden /> Share
          </Key>{" "}
          at the bottom of Safari (at the top on iPad).
        </Step>
        <Step n={2}>
          Scroll down and tap{" "}
          <Key>
            <PlusSquare className="size-3.5" aria-hidden /> Add to Home Screen
          </Key>
          .
        </Step>
        <Step n={3}>
          Tap <Key>Add</Key>. RealityStore appears on your home screen and opens full screen, like any other app.
        </Step>
      </ol>
    );
  } else if (platform === "ios-other") {
    body = (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">On iPhone and iPad, apps like this can only be added from Safari.</p>
        <ol className="flex flex-col gap-4">
          <Step n={1}>Copy the link below.</Step>
          <Step n={2}>
            Open <Key>Safari</Key> and paste it into the address bar.
          </Step>
          <Step n={3}>
            Tap{" "}
            <Key>
              <Share className="size-3.5" aria-hidden /> Share
            </Key>
            , then{" "}
            <Key>
              <PlusSquare className="size-3.5" aria-hidden /> Add to Home Screen
            </Key>
            .
          </Step>
        </ol>
        <p className="rounded-xl border border-line bg-fg/[0.03] px-3 py-2 text-center text-sm text-ink">{host}</p>
        <CopyLink />
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">This page is open inside another app. Install from your phone's browser instead.</p>
        <ol className="flex flex-col gap-4">
          <Step n={1}>
            Tap{" "}
            <Key>
              <MoreVertical className="size-3.5" aria-hidden />
            </Key>{" "}
            in the corner and choose <Key>Open in Chrome</Key> (or <Key>Open in browser</Key>).
          </Step>
          <Step n={2}>
            In Chrome, tap <Key>Install app</Key> when it appears, or{" "}
            <Key>
              <MoreVertical className="size-3.5" aria-hidden />
            </Key>{" "}
            then <Key>Add to Home screen</Key>.
          </Step>
        </ol>
        <p className="rounded-xl border border-line bg-fg/[0.03] px-3 py-2 text-center text-sm text-ink">{host}</p>
        <CopyLink />
      </div>
    );
  }

  return (
    <Sheet open={open} onClose={close} title="Add RealityStore to your home screen" description="Free, and no app store needed.">
      {body}
      <GlowButton className="mt-6" fullWidth onClick={close}>
        Got it
      </GlowButton>
    </Sheet>
  );
}
